import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import useI18n from "../../../../../hooks/useTranslations";
import { NormativeVersion, NormativeVersionType } from "../../../../../http/patrol/types/normativeVersions";
import { useNormativeVersionsApi } from "../../../../../http/patrol/useNormativeVersions";
import { usePatrolSystemsApi } from "../../../../../http/patrol/usePatrolSystems";
import Button from "../../../../ui/Button";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import Input from "../../../../ui/Input";
import MultiCheckbox from "../../../../ui/MultiCheckbox";
import VisibilityScope, { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import { CustomSelect } from "../../../../ui/select/CustomSelect";

type normativeVersionType = {
  value: NormativeVersionType;
  label: string;
};

interface NormativeVersionFormFormBody {
  name: string;
  type: normativeVersionType;
  visibility: VisibilityScopeValue;
  systemIds: string[];
}

interface NormativeVersionProps {
  normativeId: string;
  item?: NormativeVersion;
  isLoading?: boolean;
  onClose: () => void;
}

const NormativeVersionForm: FC<NormativeVersionProps> = ({ item, isLoading, normativeId, onClose }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const normativeVersionsApi = useNormativeVersionsApi();
  const aborter = useRef(new AbortController());
  const patrolSystemsApi = usePatrolSystemsApi();

  const patrolSystems = patrolSystemsApi.useAdminPatrolSystems({ limit: 100 }, aborter.current.signal);

  const types: normativeVersionType[] = [
    {
      value: "anchor-point",
      label: i18n.translation.anchorPoints.singular,
    },
    {
      value: "life-line",
      label: i18n.translation.common.lifeLine,
    },
  ];

  const defaultvalues = {
    ...item,
    visibility: item?.visibility,
    systemIds: item?.systems?.map((x) => x.id) ?? [],
    type: types.find((t) => t.value === item?.type),
  };

  const onSubmit =
    () =>
    async (formData: NormativeVersionFormFormBody): Promise<void> => {
      setIsSubmitting(true);
      const body = {
        name: formData.name,
        visibility: formData.visibility,
        type: formData.type.value,
        systemIds: formData.systemIds,
      };
      if (item?.id) {
        try {
          // await sleep();
          await normativeVersionsApi.updateNormativeVersion(normativeId, item.id, body);
          onClose();
        } catch (error) {
          dispatch(showErrorNotification(i18n, error));
        } finally {
          setIsSubmitting(false);
        }
      } else {
        try {
          await normativeVersionsApi.insertNormativeVersion(normativeId, body);
          onClose();
        } catch (error) {
          dispatch(showErrorNotification(i18n, error));
        } finally {
          setIsSubmitting(false);
        }
      }
    };

  return (
    <Form<NormativeVersionFormFormBody>
      submitText={i18n.translation.common.save}
      defaultValues={defaultvalues}
      onSubmit={onSubmit}
      disabled={isSubmitting}
      secondaryButton={
        <Button
          buttonProps={{
            onClick: () => onClose(),
          }}
          type="secondary"
        >
          {i18n.translation.common.cancel}
        </Button>
      }
    >
      {({ control, setValue, reset, register, formState: { errors }, watch }) => {
        const systemIds = watch("systemIds");

        useEffect(() => {
          item && reset(defaultvalues);
        }, [defaultvalues]);

        return (
          <FormFieldsContainer fullWidth>
            <FormField label={i18n.translation.common.name} error={errors.name}>
              {({ labelId, isOptional, isInvalid }) => {
                const options = {
                  ...register("name", {
                    required: !isOptional,
                    minLength: 1,
                  }),
                };
                return <Input id={labelId} isInvalid={isInvalid} disabled={isSubmitting} {...options} />;
              }}
            </FormField>
            <Controller
              name="type"
              control={control}
              rules={{
                required: true,
              }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.type} error={fieldState.error}>
                  {({ labelId }) => (
                    <CustomSelect<normativeVersionType>
                      {...field}
                      inputId={labelId}
                      options={types}
                      isDisabled={isSubmitting || isLoading}
                      isLoading={isSubmitting || isLoading}
                      getOptionLabel={(x) => x.label}
                      getOptionValue={(x) => x.value}
                      onChange={(value) => setValue("type", value!)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="systemIds"
              control={control}
              rules={{
                validate: (value) => {
                  if (!value?.length) {
                    return i18n.translation.normativeVersions.errors.atLeastOneSystemRequired;
                  }
                  return undefined;
                },
              }}
              render={({ fieldState }) => (
                <FormField label={i18n.translation.systems.plural} error={fieldState.error}>
                  <MultiCheckbox
                    disabled={isSubmitting}
                    values={
                      patrolSystems.data?.items.map((x) => ({
                        id: x.id,
                        title: x.name,
                        selected: systemIds.includes(x.id),
                      })) ?? []
                    }
                    onUpdate={(id, checked) => {
                      setValue("systemIds", checked ? [...systemIds, id] : systemIds.filter((x) => x !== id));
                    }}
                  />
                </FormField>
              )}
            />
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <VisibilityScope {...field} inputId={labelId} onChange={(value) => setValue("visibility", value)} />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default NormativeVersionForm;
